/* eslint-disable */
// Session 49 · Reusable external-booking editor.
// Renders the full Booking Method radio + destination fields + live
// preview + Test Link + URL validation, so both AdminWorkspaceEdit and
// AdminVenueEdit stay lean.
//
// Props:
//   form                current form state ({ management_mode, external_booking_url, ... })
//   set(key, val)       updater used everywhere else in the parent form
//   slug                workspace or venue slug (used for UTM utm_content)
//   scope               "workspace" | "venue"  (labels + tracking source)
//
// The parent's save flow already ships the whole `form` object; we do
// not need any extra plumbing beyond adding the new keys to the
// backend WorkspaceEditIn/VenueIn models (done).
import { useMemo, useState } from "react";

const UTM_DEFAULTS = {
  utm_source: "metier",
  utm_medium: "marketplace",
  utm_campaign: "workspace_booking",
};

/** Return { valid: bool, reason: str } for a booking URL. */
function validateUrl(raw) {
  const s = (raw || "").trim();
  if (!s) return { valid: false, reason: "URL is required" };
  try {
    const u = new URL(s);
    if (u.protocol !== "http:" && u.protocol !== "https:") {
      return { valid: false, reason: "Must start with http:// or https://" };
    }
    if (!u.hostname.includes(".")) {
      return { valid: false, reason: "Hostname looks invalid" };
    }
    return { valid: true, reason: "" };
  } catch {
    return { valid: false, reason: "Not a valid URL" };
  }
}

function withUtm(rawUrl, slug) {
  try {
    const u = new URL(rawUrl);
    for (const [k, v] of Object.entries(UTM_DEFAULTS)) {
      if (!u.searchParams.has(k)) u.searchParams.set(k, v);
    }
    if (slug && !u.searchParams.has("utm_content")) {
      u.searchParams.set("utm_content", slug);
    }
    return u.toString();
  } catch {
    return rawUrl;
  }
}

export default function ExternalBookingSection({ form, set, slug = "", scope = "workspace" }) {
  const mode = form.management_mode || "metier_managed";
  const isExternal = mode === "external_link";
  const url = form.external_booking_url || "";
  const validation = useMemo(() => validateUrl(url), [url]);
  const finalUrl = useMemo(() => (validation.valid ? withUtm(url, slug) : url), [url, slug, validation.valid]);
  const [showTest, setShowTest] = useState(false);

  const setMode = (m) => {
    set("management_mode", m);
    // If flipping to external and there's no URL yet, gently focus the input via UX only.
  };

  return (
    <div
      className="m-card"
      style={{ padding: 18, marginTop: 16, border: "1px solid rgba(27,35,64,0.10)", borderRadius: 10 }}
      data-testid={`${scope}-external-booking-section`}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
        <div>
          <div className="m-eyebrow" style={{ color: "var(--gold)" }}>
            {scope === "venue" ? "Venue booking" : "Workspace booking"}
          </div>
          <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 20, margin: "4px 0 0" }}>
            Booking method
          </h3>
        </div>
        <div className="m-muted small" style={{ maxWidth: 320, textAlign: "right" }}>
          Choose how members complete their reservation.
          {scope === "venue" && " Child workspaces can override this per listing."}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
        {[
          { key: "metier_managed", title: "Book through Métier", desc: "Full checkout, payment, deposit + balance managed by Métier via Stripe Connect." },
          { key: "external_link", title: "External booking", desc: "Send members to the partner's own booking page. Métier tracks the click with UTM analytics." },
        ].map(opt => (
          <label
            key={opt.key}
            style={{
              display: "flex", gap: 10, padding: 14,
              border: "1px solid " + (mode === opt.key ? "var(--gold)" : "rgba(27,35,64,0.14)"),
              background: mode === opt.key ? "rgba(184,145,58,0.06)" : "#fff",
              borderRadius: 8, cursor: "pointer",
              transition: "background 100ms, border 100ms",
            }}
            data-testid={`${scope}-booking-method-${opt.key}`}
          >
            <input
              type="radio"
              name={`${scope}-booking-method`}
              checked={mode === opt.key}
              onChange={() => setMode(opt.key)}
              style={{ marginTop: 3 }}
            />
            <span style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: "var(--m-navy)", marginBottom: 4 }}>{opt.title}</div>
              <div className="m-muted small" style={{ lineHeight: 1.4 }}>{opt.desc}</div>
            </span>
          </label>
        ))}
      </div>

      {isExternal && (
        <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="m-eyebrow" style={{ fontSize: 10, letterSpacing: "0.14em" }}>Partner booking URL</span>
            <input
              type="url"
              inputMode="url"
              value={url}
              onChange={(e) => set("external_booking_url", e.target.value)}
              placeholder="https://partner-site.com/booking/room-12"
              data-testid={`${scope}-external-url-input`}
              style={{
                padding: "9px 12px", fontSize: 14,
                border: "1px solid " + (url && !validation.valid ? "rgba(180,60,60,0.55)" : "rgba(27,35,64,0.18)"),
                borderRadius: 6, background: "#fff",
              }}
            />
            {url && !validation.valid && (
              <span style={{ color: "rgb(180,60,60)", fontSize: 12 }}
                    data-testid={`${scope}-external-url-error`}>
                ⚠ {validation.reason}
              </span>
            )}
          </label>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="m-eyebrow" style={{ fontSize: 10, letterSpacing: "0.14em" }}>Button text (optional)</span>
              <input
                type="text"
                value={form.booking_cta_override || ""}
                onChange={(e) => set("booking_cta_override", e.target.value)}
                placeholder="Book Direct →"
                maxLength={64}
                data-testid={`${scope}-booking-cta-input`}
                style={{ padding: "9px 12px", fontSize: 14,
                          border: "1px solid rgba(27,35,64,0.18)",
                          borderRadius: 6, background: "#fff" }}
              />
              <span className="m-muted small">Defaults to "Book Direct →" if left blank.</span>
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="m-eyebrow" style={{ fontSize: 10, letterSpacing: "0.14em" }}>Disclosure line (optional)</span>
              <input
                type="text"
                value={form.cta_disclosure || ""}
                onChange={(e) => set("cta_disclosure", e.target.value)}
                placeholder="You'll complete your reservation securely on our partner's website."
                maxLength={280}
                data-testid={`${scope}-cta-disclosure-input`}
                style={{ padding: "9px 12px", fontSize: 14,
                          border: "1px solid rgba(27,35,64,0.18)",
                          borderRadius: 6, background: "#fff" }}
              />
            </label>
          </div>

          <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={form.open_in_new_tab !== false}
                onChange={(e) => set("open_in_new_tab", e.target.checked)}
                data-testid={`${scope}-open-in-new-tab`}
              />
              Open in new tab
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={form.track_external_clicks !== false}
                onChange={(e) => set("track_external_clicks", e.target.checked)}
                data-testid={`${scope}-track-clicks`}
              />
              Track outbound clicks + UTM analytics
            </label>
          </div>

          {/* Live preview + Test Link ---------------------------------- */}
          <div
            style={{
              marginTop: 6,
              padding: 14,
              background: "#faf8f3",
              border: "1px solid rgba(27,35,64,0.10)",
              borderRadius: 8,
            }}
            data-testid={`${scope}-booking-destination-preview`}
          >
            <div className="m-eyebrow" style={{ fontSize: 10, letterSpacing: "0.14em", color: "rgba(27,35,64,0.55)", marginBottom: 6 }}>
              Final destination (UTM-tagged)
            </div>
            <div
              style={{
                fontFamily: "ui-monospace, 'SF Mono', 'Menlo', monospace",
                fontSize: 12, lineHeight: 1.5,
                wordBreak: "break-all",
                color: validation.valid ? "var(--m-navy)" : "rgba(27,35,64,0.45)",
              }}
              data-testid={`${scope}-booking-final-url`}
            >
              {url ? finalUrl : "— add a URL above to preview —"}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
              <a
                href={validation.valid ? finalUrl : undefined}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { if (!validation.valid) { e.preventDefault(); return; } setShowTest(true); }}
                className="m-btn-ghost"
                style={{
                  fontSize: 12, padding: "6px 12px", textDecoration: "none",
                  opacity: validation.valid ? 1 : 0.4,
                  pointerEvents: validation.valid ? "auto" : "none",
                  borderColor: "var(--gold)", color: "var(--gold)",
                }}
                data-testid={`${scope}-booking-test-link`}
              >
                ↗ Test link
              </a>
              <span className="m-muted small">
                Adds <code>utm_source=metier</code>, <code>utm_medium=marketplace</code>, <code>utm_campaign=workspace_booking</code>, and
                {slug ? <> <code>utm_content={slug}</code></> : <> a <code>utm_content=&lt;slug&gt;</code> once saved</>}.
              </span>
              {showTest && <span className="m-muted small" data-testid={`${scope}-booking-test-opened`} style={{ color: "var(--gold)" }}>opened in a new tab ✓</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
